import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { localize } from 'wanderu-i18n';
import {
    AC,
    Attendant,
    ExtraLegroom,
    Games,
    Green,
    HotBeverage,
    LoyaltyRewards,
    Meal,
    Movies,
    Newspaper,
    PowerOutlet,
    QRCode,
    Restroom,
    SatelliteTV,
    Seatbelts,
    Snacks,
    USBPower,
    Water,
    Wifi,
} from 'wanderu-icons';

import TooltipController from '../Tooltip/Controller/Controller';
import TooltipSelect from '../Tooltip/Select/Select';

import styles from './Features.scss';

const FEATURE_MAP = {
    air_conditioning: AC,
    attendant: Attendant,
    bathroom: Restroom,
    beverage: HotBeverage,
    extra_legroom: ExtraLegroom,
    free_water: Water,
    games: Games,
    green_cert: Green,
    hot_beverage: HotBeverage,
    loyalty_reward: LoyaltyRewards,
    meal: Meal,
    movie: Movies,
    newspaper_magazine: Newspaper,
    power_outlet: PowerOutlet,
    qr_code: QRCode,
    satellite_tv: SatelliteTV,
    seat_belts: Seatbelts,
    snacks: Snacks,
    toilet: Restroom,
    toilets: Restroom,
    usb_power: USBPower,
    wifi: Wifi,
};

function FeatureIcon(props) {
    const {
        feature,
        featureNameStyle,
        showFeatureName,
        showTooltips,
        t,
    } = props;
    const containerClassName = classNames(
        styles.feature,
        featureNameStyle,
        { [styles.withName]: showFeatureName },
    );
    const iconClassName = classNames(
        styles.img,
        { [styles.withName]: showFeatureName },
    );
    const featureString = t(`baseApp:${feature}_features`);
    const Icon = FEATURE_MAP[feature];

    if (!Icon) {
        return null;
    }

    return (
        <span
            className={containerClassName}
            role="listitem"
        >
            <TooltipController
                className={styles.tooltip}
                disabled={!showTooltips}
                offsetX="center"
            >
                <span>
                    {featureString}
                </span>
                <TooltipSelect>
                    <Icon className={iconClassName} title={featureString} />
                    {showFeatureName && (
                        <span className={styles.name}>
                            {featureString}
                        </span>
                    )}
                </TooltipSelect>
            </TooltipController>
        </span>
    );
}

FeatureIcon.propTypes = {
    feature: PropTypes.string.isRequired,
    featureNameStyle: PropTypes.string,
    showFeatureName: PropTypes.bool,
    showTooltips: PropTypes.bool,
    t: PropTypes.func,
};

const LocalizedFeatureIcon = localize(FeatureIcon);

function Features(props) {
    const {
        className,
        featureNameStyle,
        features,
        showFeatureNameNextToIcon,
        showTooltips,
    } = props;

    const featuresWithIcons = features.filter((feature) => FEATURE_MAP[feature]);

    return (
        <div
            className={`${styles.featuresContainer} ${className}`}
            role="list"
        >
            {featuresWithIcons.map((feature) => (
                <LocalizedFeatureIcon
                    feature={feature}
                    featureNameStyle={featureNameStyle}
                    key={feature}
                    showFeatureName={showFeatureNameNextToIcon}
                    showTooltips={showTooltips}
                />
            ))}
        </div>
    );
}

Features.propTypes = {
    className: PropTypes.string,
    featureNameStyle: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.string).isRequired,
    showFeatureNameNextToIcon: PropTypes.bool,
    showTooltips: PropTypes.bool,
};

Features.defaultProps = {
    className: '',
    featureNameStyle: '',
    showFeatureNameNextToIcon: false,
    showTooltips: true,
};

export {
    LocalizedFeatureIcon as FeatureIcon,
    Features,
};
export default Features;
